<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="computedTitle"
    dialog-width="50%"
    max-height="80vh"
    body-height="70vh"
    @agree="agreeAction"
    :loading="loading"
    :is-valid="isValid"
    :agree-text="computedAgree"
  >
    <template #body>
      <template v-if="isFetched">
        <v-col cols="12">
          <v-form ref="form" v-model="isValid">
            <v-checkbox
              v-if="
                isCopying ? ($refs['expressionBuilder'] ? $refs['expressionBuilder'].$data.isLoaded : false) : false
              "
              class="mt-0"
              v-model="copyWithDatasource"
            >
              <template #label>
                <div class="text-body-2 text-left" style="color: #000">
                  {{ $i18n.t('dialog.body.copyWithDatasource') }}
                </div>
              </template>
            </v-checkbox>
            <expression-builder
              ref="expressionBuilder"
              v-model="payload.filter_expression"
              :mainDataSource.sync="payload.data_source"
              :type="scope"
              :data-source-input-metadata="{
                ...(isCopying && copyWithDatasource && { label: $i18n.t('dialog.body.copiedDataSource') }),
              }"
              :is-data-source-changable="isDataSourceChangable"
              admin
            >
              <template #inputs>
                <v-row no-gutters>
                  <v-col cols="9">
                    <main-input-dialog-row
                      v-for="input in inputs"
                      v-bind="input"
                      :key="input.name"
                      v-model="payload[input.name]"
                    >
                      <template #image_attributes.input="data">
                        <v-col cols="6">
                          <v-row no-gutters>
                            <v-col cols="12">
                              <data-input v-bind="data" v-model="payload[input.name]" />
                            </v-col>
                          </v-row>
                          <v-row no-gutters v-if="payload[input.name]?.length > 1">
                            <v-checkbox class="mt-0 pt-0" v-model="payload['as_gallery']">
                              <template #label>
                                <div class="text-body-2 text-left" style="color: #202124">
                                  {{ $i18n.t('dialog.body.profilePictureGalery') }}
                                </div>
                              </template>
                            </v-checkbox>
                          </v-row>
                        </v-col>
                      </template>
                    </main-input-dialog-row>
                  </v-col>
                </v-row>
              </template>
            </expression-builder>
            <layer-adding-permissions
              class="mt-5"
              :value="payload.permissions"
              @input="onInput"
              @mainInput="onMainInput"
              @attachmentsInput="onAttachmentsInput"
              type="features_layer"
            />
          </v-form>
        </v-col>
      </template>
      <main-loader v-else></main-loader>
    </template>
  </main-large-dialog>
</template>

<script>
import { call, get } from 'vuex-pathify';

import MainInputDialogRow from '@/components/MainInputDialogRow';
import ExpressionBuilder from '@/components/ExpressionBuilder';
import MainLoader from '@/components/MainLoader';
import LayerAddingPermissions from '@/components/LayerAddingPermissions';
import MainLargeDialog from '@/components/MainLargeDialog';
import DataInput from '@/components/DataInput';

export default {
  name: 'LayerAddingDialog',
  components: {
    MainLargeDialog,
    MainInputDialogRow,
    ExpressionBuilder,
    MainLoader,
    LayerAddingPermissions,
    DataInput,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    scope: {
      type: String,
      default: '',
    },
    layer: {
      type: Object,
      default: undefined,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    isCopying: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isFetched: false,
    loading: false,
    isFiltersVisible: false,
    isValid: false,
    currentLayerObject: undefined,
    filter: undefined,
    copyWithDatasource: false,
    payload: {
      name: '',
      description: '',
      image_attributes: [],
      as_gallery: false,
      smart_match: null,
      filter_expression: null,
      style_web: {},
      data_source: undefined,
      permissions: [],
    },
    isDataSourceChangable: false,
  }),
  computed: {
    profiles: get('smartMatch/profiles'),
    datasourceNameMaxLength: get('admin/settingsValues@datasource_name_max_length'),
    featureLayerNameMaxLength: get('admin/settingsValues@feature_layer_name_max_length'),
    isSmartMatchEnabled: get('admin/settingsValues@smart_match_module_enabled'),
    isSmartMatchEditEnabled: get('admin/additionalModules@SMART_MATCH_EDIT_MODULE.enabled'),
    computedTitle() {
      return this.isEditing
        ? this.$i18n.t('dialog.title.manageLayer')
        : this.isCopying
        ? this.$i18n.t('dialog.title.copyLayer', { layer_name: this.layer?.name })
        : this.$i18n.t('dialog.title.addLayer');
    },
    computedAgree() {
      return this.isEditing ? 'dialog.agreeSave' : this.isCopying ? 'dialog.agreeCreate' : 'dialog.agreeAdd';
    },
    agreeAction() {
      return this.isEditing ? this.editLayer : this.isCopying ? this.copyLayer : this.addLayer;
    },
    groups: get('users/groups'),
    inputs() {
      return [
        ...(this.isCopying && this.copyWithDatasource
          ? [
              {
                name: 'datasource_name',
                cols: '12',
                clearable: true,
                align: 'baseline',
                translationPath: 'dialog.body.dataSourceName',
                rules: [v => !!v || 'rules.required'],
                dataType: {
                  name: 'text',
                  max_length: this.datasourceNameMaxLength,
                },
              },
            ]
          : []),
        {
          name: 'name',
          cols: '12',
          clearable: true,
          align: 'baseline',
          translationPath: 'dialog.body.layerName',
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: this.featureLayerNameMaxLength,
          },
        },
        {
          name: 'description',
          cols: '12',
          clearable: true,
          align: 'baseline',
          translationPath: 'dialog.body.layerDescription',
          subtitle: 'dialog.body.layerDescriptionSubtitle',
          rows: 3,
          dataType: {
            name: 'textarea',
          },
        },
        {
          name: 'image_attributes',
          cols: '12',
          clearable: true,
          align: 'start',
          translationPath: 'dialog.body.profilePicture',
          subtitle: 'dialog.body.profilePictureSubtitle',
          dataType: {
            name: 'select',
          },
          multiple: true,
          items: this.selectedDataSourceHyperlinkAttributes,
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.payload.data_source,
          hideDetails: this.payload['image_attributes']?.length > 1,
        },
        ...(this.isSmartMatchEnabled && this.isSmartMatchEditEnabled
          ? [
              {
                name: 'smart_match',
                cols: '12',
                clearable: true,
                align: 'start',
                translationPath: 'dialog.body.smartMatchMultilanguage',
                subtitle: 'dialog.body.smartMatchMultilanguageSubtitle',
                dataType: {
                  name: 'select',
                },
                items: this.profiles
                  ? Object.values(this.profiles).filter(profile => profile.datasource === this.payload.data_source)
                  : [],
                itemText: 'name',
                itemValue: 'id',
                disabled: !this.payload.data_source,
              },
            ]
          : []),
      ];
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    dataSources: get('layers/metadata'),
    selectedDataSource() {
      return this.dataSources[this.payload.data_source] || {};
    },
    selectedDataSourceHyperlinkAttributes() {
      return (
        this.selectedDataSource?.attributes_schema?.attributes?.filter(attr => attr.data_type.name === 'hyperlink') ||
        []
      );
    },
  },
  watch: {
    computedIsVisible: {
      immediate: true,
      handler(nV) {
        if (!nV) {
          this.$refs.form?.reset();
          this.$refs.form?.resetValidation();
          this.$refs.expressionBuilder?.reset();
          this.isFetched = false;
          this.isValid = false;
          this.$emit('update:layer', undefined);
        } else {
          this.init();
        }
      },
    },
  },
  methods: {
    copyDataSourceData: call('layers/copyDataSourceData'),
    addDataSource: call('layers/addDataSource'),
    getLayerPermissions: call('layers/getLayerPermissions'),
    addLayerAction: call('layers/addLayer'),
    editLayerAction: call('layers/editLayer'),
    getGroups: call('users/getGroups'),
    getProfiles: call('smartMatch/getProfiles'),
    deleteDataSource: call('layers/deleteDataSource'),
    onInput(e) {
      const index = this.payload.permissions.map(permission => permission.group_id).indexOf(e.group_id);
      this.payload.permissions.splice(index, 1, { ...e });
    },
    onAttachmentsInput(v) {
      this.payload.permissions = this.payload.permissions.map(permission => {
        return { ...permission, attachments_and_comments_value: v };
      });
    },
    onMainInput(v) {
      this.payload.permissions = this.payload.permissions.map(permission => {
        return { ...permission, main_value: v };
      });
    },
    getPermissions() {
      return this.$_objectToArray(this.groups)
        .filter(group => !group.is_admin_group)
        .map(group => {
          return { group_id: group.id, main_value: 0, attachments_and_comments_value: 0 };
        });
    },
    async addEditHandler(f, payload) {
      try {
        this.loading = true;
        await f(payload);
        this.loading = false;
        this.computedIsVisible = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    async editLayer() {
      const payloadCopy = { ...this.payload };
      delete payloadCopy.data_source;
      await this.addEditHandler(this.editLayerAction, {
        layer_id: this.layer.id,
        scope: this.scope,
        layer_type: 'features_layers',
        body: {
          ...payloadCopy,
        },
      });
    },
    async addLayer() {
      delete this.payload.datasource_name;
      await this.addEditHandler(this.addLayerAction, {
        layer_type: 'features_layers',
        body: {
          ...this.payload,
          layer_scope: this.scope,
        },
      });
    },
    async copyLayer() {
      await this.addEditHandler(this.copyLayerAction, {
        addPayload: {
          layer_type: 'features_layers',
          body: {
            ...this.payload,
            layer_scope: this.scope,
          },
        },
        editPayload: {
          scope: this.scope,
          layer_type: 'features_layers',
          body: {
            form_schema: this.layer.form_schema,
          },
        },
      });
    },
    async copyLayerAction(data) {
      let newData = JSON.parse(JSON.stringify(data));
      try {
        if (this.copyWithDatasource) {
          delete newData.addPayload.body.smart_match;
          const r = await this.addDataSource({
            skipMetadataParsing: true,
            payload: this.getCopyDatasourceAttributes(this.selectedDataSource),
          });
          const newDatasourceName = r.datasource_metadata.name;
          let isCopySuccessful = true;
          try {
            await this.copyDataSourceData({
              fromDataSource: this.selectedDataSource?.name,
              toDataSource: newDatasourceName,
            });
          } catch {
            isCopySuccessful = false;
            try {
              await this.deleteDataSource(newDatasourceName);
            } catch {
              //
            }
          }
          if (!isCopySuccessful) return;
          newData.addPayload.body.filter_expression = this.$_replaceSubstringInNestedObject(
            newData.addPayload.body.filter_expression,
            this.selectedDataSource?.name,
            newDatasourceName,
            'value'
          );
          newData.addPayload.body.data_source = newDatasourceName;
        }
        delete newData.addPayload.body.datasource_name;
        const newLayerId = await this.addLayerAction(newData.addPayload);
        await this.editLayerAction({
          ...newData.editPayload,
          layer_id: newLayerId,
        });
      } catch {
        //
      }
    },
    getCopyDatasourceAttributes(datasourceMetadata) {
      const attributes = JSON.parse(JSON.stringify(datasourceMetadata)).attributes_schema.attributes;
      const datasourceCopyPayload = {};
      datasourceCopyPayload.datasource_verbose_name = this.payload.datasource_name;
      datasourceCopyPayload.attributes = [];
      datasourceCopyPayload.srid = datasourceMetadata.srid;
      datasourceCopyPayload.geometry_type = datasourceMetadata.geometry_type;
      attributes
        .filter(
          attr =>
            !attr.predefined && !attr.read_only && !['id'].includes(attr.name) && attr.data_type.name !== 'geometric'
        )
        .forEach(attr => {
          const { verbose_name, name, data_type, relation, measurement_unit, dict_id } = attr;
          delete data_type.scada3_display_attribute;
          datasourceCopyPayload.attributes.push({
            verbose_name,
            name,
            data_type,
            ...(dict_id && { dict_id }),
            ...(measurement_unit && { measurement_unit_id: measurement_unit.id }),
            ...(relation && { relation }),
          });
        });
      return datasourceCopyPayload;
    },
    async init() {
      try {
        await Promise.all([
          this.getGroups(),
          ...(this.isSmartMatchEnabled && this.isSmartMatchEditEnabled ? [this.getProfiles()] : []),
        ]);
        if (this.isEditing || this.isCopying) {
          const {
            name,
            filter_expression,
            data_source_name,
            id,
            description,
            style_web,
            image_attributes,
            as_gallery,
            smart_match,
          } = this.layer;
          this.payload = {
            ...this.payload,
            name: this.isCopying ? null : name,
            filter_expression,
            data_source: data_source_name,
            permissions: [],
            description,
            image_attributes,
            as_gallery,
            smart_match,
            ...(this.isCopying && { style_web }),
          };
          this.isDataSourceChangable = false;
          this.payload.permissions = JSON.parse(JSON.stringify((await this.getLayerPermissions(id)).data.data));
        } else {
          this.payload.permissions = JSON.parse(JSON.stringify(this.getPermissions()));
          this.isDataSourceChangable = true;
        }
        this.isFetched = true;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-list-item__title {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  text-align: left;
}
</style>
